import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdDelete, MdDownload } from 'react-icons/md';

import { usePersonsMap } from '@/hooks/usePersonsMap';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import type { DailyLog } from '@/store/vessels/vessel.dto';
import { useVessels } from '@/store/vessels/vessels.store';
import { downloadBlob } from '@/utils/download-file-from-link';
import { getBrowserTimeZone } from '@/utils/timezone';
import ActionsButton from '@components/ActionsButton/ActionsButton.tsx';
import DataTable, { type Column } from '@components/DataTable/DataTable.tsx';

type DailyLogRow = DailyLog & { id: string };

const formatNameDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
};

const formatSignedAt = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type Props = {
  search?: string;
  onSelectLog?: (iso: string) => void;
};

export default function DailyLogHistory({ search = '', onSelectLog }: Props) {
  const getDailyLogs = useVessels((s) => s.getDailyLogs);
  const getDailyLogPdf = useVessels((s) => s.getDailyLogPdf);
  const deleteDailyLog = useVessels((s) => s.deleteDailyLog);
  const personsMap = usePersonsMap();
  const { can } = usePermissions();
  const canDelete = can(PERMISSIONS.logbook.DailyLogDelete);

  const [logs, setLogs] = useState<DailyLogRow[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const renderSignedCell = useCallback(
    (personId: string | null, signedAt: string | null) => {
      if (!signedAt) return '-';
      const person = personId ? personsMap.get(personId) : undefined;
      const name = person
        ? `${person.person_info?.first_name ?? ''} ${person.person_info?.last_name ?? ''}`.trim() ||
          'Unnamed'
        : 'Unknown';
      return `${name} · ${formatSignedAt(signedAt)}`;
    },
    [personsMap],
  );

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((row) => {
      const date = row.header.date ?? '';
      const formatted = formatNameDate(date);
      return (
        formatted.toLowerCase().includes(q) ||
        date.toLowerCase().includes(q) ||
        `daily log ${formatted}`.toLowerCase().includes(q)
      );
    });
  }, [logs, search]);

  useEffect(() => {
    void (async () => {
      const items = await getDailyLogs();
      setLogs(items.filter((l): l is DailyLogRow => Boolean(l.id)));
    })();
  }, [getDailyLogs]);

  const handleDownload = useCallback(
    async (row: DailyLogRow) => {
      setDownloadingId(row.id);
      try {
        const blob = await getDailyLogPdf(row.id, getBrowserTimeZone());
        await downloadBlob(blob, `daily-log-${row.header.date ?? row.id}.pdf`);
      } finally {
        setDownloadingId(null);
      }
    },
    [getDailyLogPdf],
  );

  const handleDelete = useCallback(
    async (row: DailyLogRow) => {
      await deleteDailyLog(row.id);
      setLogs((prev) => prev.filter((l) => l.id !== row.id));
    },
    [deleteDailyLog],
  );

  const columns: Column<DailyLogRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        sortValue: (row) => row.header.date ?? '',
        render: (_v, row) => `Daily Log ${formatNameDate(row.header.date)}`,
      },
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        sortValue: (row) => row.header.date ?? '',
        render: (_v, row) => formatNameDate(row.header.date),
      },
      {
        key: 'captainSigned',
        label: 'Captain Signed',
        sortable: true,
        sortValue: (row) => row.signatures?.captain_signed_at ?? '',
        render: (_v, row) =>
          renderSignedCell(row.signatures?.captain_id, row.signatures?.captain_signed_at),
      },
      {
        key: 'officerSigned',
        label: 'Officer Signed',
        sortable: true,
        sortValue: (row) => row.signatures?.first_officer_signed_at ?? '',
        render: (_v, row) =>
          renderSignedCell(
            row.signatures?.first_officer_id,
            row.signatures?.first_officer_signed_at,
          ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: '120px',
        render: (_v, row) => (
          <ActionsButton
            ariaLabel="Daily log actions"
            items={[
              {
                label: downloadingId === row.id ? 'Downloading…' : 'Download PDF',
                icon: <MdDownload />,
                onClick: () => void handleDownload(row),
              },
              ...(canDelete
                ? [
                    {
                      label: 'Delete',
                      icon: <MdDelete />,
                      variant: 'danger' as const,
                      onClick: () => void handleDelete(row),
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    [canDelete, downloadingId, handleDelete, handleDownload, renderSignedCell],
  );

  return (
    <DataTable
      autoFit={false}
      data={filteredLogs}
      columns={columns}
      itemsPerPage={10}
      itemsPerBigPage={10}
      onRowClick={onSelectLog ? (row) => onSelectLog(row.header.date) : undefined}
    />
  );
}
